/**
 * Onboarding templates. Templates are data (also seeded into the `templates` table) and
 * `instantiateTemplate` turns one into concrete rows for a user.
 */
import { BUILTIN_DEFAULT_REMINDERS } from './reminders.ts';
import { POSITION_STEP } from './position.ts';
import type {
  Instant,
  List,
  SpaceBlueprint,
  Space,
  Status,
  Tag,
  TemplateDefinition,
  UUID,
  Workspace,
} from './types.ts';

export const SPACE_COLORS = [
  '#16a34a',
  '#ea580c',
  '#7c3aed',
  '#0f172a',
  '#0891b2',
  '#db2777',
  '#ca8a04',
  '#2563eb',
] as const;

export const DEFAULT_STATUSES: SpaceBlueprint['statuses'] = [
  { name: 'TO DO', color: '#87909e', type: 'not_started' },
  { name: 'IN PROGRESS', color: '#3b82f6', type: 'active' },
  { name: 'WAITING', color: '#f59e0b', type: 'active' },
  { name: 'COMPLETE', color: '#16a34a', type: 'closed' },
];

export const METHOD_TEMPLATE: TemplateDefinition = {
  repeatable: true,
  spaces: [
    {
      name: 'Personal',
      color: SPACE_COLORS[2],
      lists: [
        { name: 'Priority Tasks', emoji: '🎯' },
        { name: 'Quick Wins', emoji: '⚡' },
        { name: 'Follow-ups', emoji: '🔁' },
        { name: 'Backlog', emoji: '📦' },
      ],
      statuses: DEFAULT_STATUSES,
      tags: [
        { name: '🎯 priority tasks', color: '#e11d48' },
        { name: '⚡ quick wins', color: '#d97706' },
        { name: '🔁 follow-ups', color: '#0284c7' },
        { name: '📦 backlog', color: '#64748b' },
      ],
    },
  ],
  default_reminders: [...BUILTIN_DEFAULT_REMINDERS],
  quick_add: { space: 0, list: 1 },
};

export const SIMPLE_TEMPLATE: TemplateDefinition = {
  spaces: [
    {
      name: 'Personal',
      color: SPACE_COLORS[2],
      lists: [{ name: 'Tasks', emoji: '✅' }],
      statuses: DEFAULT_STATUSES,
      tags: [],
    },
  ],
  default_reminders: [...BUILTIN_DEFAULT_REMINDERS],
  quick_add: { space: 0, list: 0 },
};

export const BUILTIN_TEMPLATES = [
  {
    key: 'method',
    name: '4-list method',
    description:
      'Name your Spaces; each gets 🎯 Priority Tasks · ⚡ Quick Wins · 🔁 Follow-ups · 📦 Backlog with TO DO / IN PROGRESS / WAITING / COMPLETE.',
    definition: METHOD_TEMPLATE,
  },
  {
    key: 'simple',
    name: 'Simple',
    description: 'One Space with one list. Add more any time.',
    definition: SIMPLE_TEMPLATE,
  },
] as const;

export interface InstantiateOptions {
  userId: UUID;
  newId: () => UUID;
  now: Date;
  /** For repeatable templates: one space per name. Ignored otherwise. */
  spaceNames?: string[];
  workspaceName?: string;
  /** Reuse an existing workspace instead of creating one. */
  workspaceId?: UUID;
}

export interface InstantiatedTemplate {
  workspace: Workspace | null;
  spaces: Space[];
  lists: List[];
  statuses: Status[];
  tags: Tag[];
  quickAddListId: UUID | null;
}

export function instantiateTemplate(
  def: TemplateDefinition,
  opts: InstantiateOptions,
): InstantiatedTemplate {
  const ts: Instant = opts.now.toISOString();
  const base = { user_id: opts.userId, created_at: ts, updated_at: ts, deleted_at: null };
  const workspace: Workspace | null = opts.workspaceId
    ? null
    : { ...base, id: opts.newId(), name: opts.workspaceName ?? 'My Workspace', icon: null };
  const workspaceId = opts.workspaceId ?? workspace!.id;

  const names = (opts.spaceNames ?? []).map((n) => n.trim()).filter(Boolean);
  const blueprints: SpaceBlueprint[] =
    def.repeatable && names.length > 0
      ? names.map((name, i) => ({
          ...def.spaces[0]!,
          name,
          color: SPACE_COLORS[i % SPACE_COLORS.length]!,
        }))
      : def.spaces;

  const out: InstantiatedTemplate = {
    workspace,
    spaces: [],
    lists: [],
    statuses: [],
    tags: [],
    quickAddListId: null,
  };

  blueprints.forEach((bp, si) => {
    const space: Space = {
      ...base,
      id: opts.newId(),
      workspace_id: workspaceId,
      name: bp.name,
      color: bp.color,
      icon: bp.icon ?? null,
      position: (si + 1) * POSITION_STEP,
      archived_at: null,
      clickup_id: null,
    };
    out.spaces.push(space);
    bp.statuses.forEach((st, i) =>
      out.statuses.push({
        ...base,
        id: opts.newId(),
        space_id: space.id,
        list_id: null,
        name: st.name,
        color: st.color,
        type: st.type,
        position: (i + 1) * POSITION_STEP,
      }),
    );
    bp.tags.forEach((tag) =>
      out.tags.push({
        ...base,
        id: opts.newId(),
        space_id: space.id,
        name: tag.name,
        color: tag.color,
      }),
    );
    bp.lists.forEach((l, li) => {
      const list: List = {
        ...base,
        id: opts.newId(),
        space_id: space.id,
        folder_id: null,
        name: l.name,
        emoji: l.emoji ?? null,
        color: l.color ?? null,
        position: (li + 1) * POSITION_STEP,
        archived_at: null,
        override_statuses: false,
        default_reminders: null,
        clickup_id: null,
      };
      out.lists.push(list);
      const qa = def.quick_add;
      if (qa && qa.space === si && qa.list === li) out.quickAddListId = list.id;
    });
  });

  if (!out.quickAddListId) out.quickAddListId = out.lists[0]?.id ?? null;
  return out;
}

/** Deterministic tag colour for imported tags that have none (CSV import). */
export function colorForName(name: string): string {
  const palette = [
    '#e11d48',
    '#d97706',
    '#0284c7',
    '#64748b',
    '#16a34a',
    '#7c3aed',
    '#db2777',
    '#0891b2',
  ];
  let h = 0;
  for (const ch of name.toLowerCase()) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return palette[h % palette.length]!;
}
